/**
 * Running Share API Client
 *
 * Handles sharing of running analyses and comparisons.
 */

import api from './client';

// ---------------------------------------------------------------------------
// Analysis Sharing
// ---------------------------------------------------------------------------

/**
 * Share a running analysis session.
 * @param {string} sessionId - The analysis session ID
 * @param {Object} shareData - Share configuration { is_anonymous, display_name, description }
 * @returns {Promise<{share_id: string, share_url: string, shared_at: string}>}
 */
export const shareRunningAnalysis = async (sessionId, shareData) => {
  const formData = new FormData();
  formData.append('is_anonymous', shareData.is_anonymous || false);
  if (shareData.display_name) {
    formData.append('display_name', shareData.display_name);
  }
  if (shareData.description) {
    formData.append('description', shareData.description);
  }

  const response = await api.post(`/running/analyses/${sessionId}/share`, formData);
  return response.data;
};

/**
 * Get a shared running analysis (public endpoint).
 * @param {string} shareId - The share ID (UUID)
 * @returns {Promise<Object>} Complete analysis data
 */
export const getSharedRunningAnalysis = async (shareId) => {
  const response = await api.get(`/running/shared/analysis/${shareId}`);
  return response.data;
};

/**
 * Unshare a running analysis (delete share).
 * @param {string} shareId - The share ID (UUID)
 */
export const unshareRunningAnalysis = async (shareId) => {
  await api.delete(`/running/shared/analysis/${shareId}`);
};

// ---------------------------------------------------------------------------
// Comparison Sharing
// ---------------------------------------------------------------------------

/**
 * Share a running comparison.
 * @param {Object} comparisonData - Comparison source and share config
 * @returns {Promise<{share_id: string, share_url: string, shared_at: string}>}
 */
export const shareRunningComparison = async (comparisonData) => {
  const formData = new FormData();

  // Required: base session
  formData.append('base_session_id', comparisonData.base_session_id);

  // Comparison source (one of three)
  if (comparisonData.comparison_fit_file) {
    formData.append('comparison_fit_file', comparisonData.comparison_fit_file);
  }
  if (comparisonData.comparison_file_id) {
    formData.append('comparison_file_id', comparisonData.comparison_file_id);
  }
  if (comparisonData.comparison_shared_id) {
    formData.append('comparison_shared_id', comparisonData.comparison_shared_id);
  }
  if (comparisonData.comparison_prediction_id) {
    formData.append('comparison_prediction_id', comparisonData.comparison_prediction_id);
  }

  // Options
  formData.append('snap_b_to_gpx', comparisonData.snap_b_to_gpx || false);
  formData.append('is_anonymous', comparisonData.is_anonymous || false);

  if (comparisonData.display_name) {
    formData.append('display_name', comparisonData.display_name);
  }
  if (comparisonData.description) {
    formData.append('description', comparisonData.description);
  }
  if (comparisonData.label_a) {
    formData.append('label_a', comparisonData.label_a);
  }
  if (comparisonData.label_b) {
    formData.append('label_b', comparisonData.label_b);
  }

  const response = await api.post('/running/comparisons/share', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Get a shared running comparison (public endpoint).
 * @param {string} shareId - The share ID (UUID)
 * @returns {Promise<Object>} Complete comparison data
 */
export const getSharedRunningComparison = async (shareId) => {
  const response = await api.get(`/running/shared/comparison/${shareId}`);
  return response.data;
};

/**
 * Unshare a running comparison (delete share).
 * @param {string} shareId - The share ID (UUID)
 */
export const unshareRunningComparison = async (shareId) => {
  await api.delete(`/running/shared/comparison/${shareId}`);
};
